import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth } from "./firebaseConfig";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import dayjs from "dayjs";
import logUserAction from './utils/logUserAction.js';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState("email");
  const [sortAsc, setSortAsc] = useState(true);
  const [currentUser] = useAuthState(auth);

  const fetchUsers = async () => {
    setLoading(true);
    const snapshot = await getDocs(collection(db, "users"));
    const all = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        lastLogin: data.lastLogin?.toDate?.() || null, // 👈 safely convert Firestore Timestamp to JS Date
      };
    });
    setUsers(all);
    setFiltered(all);
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let result = [...users];

    if (storeFilter) {
      result = result.filter(u => u.store === storeFilter);
    }

    if (roleFilter) {
      result = result.filter(u => u.role === roleFilter);
    }

    if (search) {
      result = result.filter(u =>
        u.email.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFiltered(result);
  }, [search, storeFilter, roleFilter, users]);

  const sortBy = (key) => {
    const newAsc = key === sortKey ? !sortAsc : true;
    const sorted = [...filtered].sort((a, b) => {
      if (!a[key]) return 1;
      if (!b[key]) return -1;
      return newAsc
        ? a[key].localeCompare?.(b[key]) ?? 0
        : b[key].localeCompare?.(a[key]) ?? 0;
    });
    setSortKey(key);
    setSortAsc(newAsc);
    setFiltered(sorted);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const ref = doc(db, "users", selectedUser.id);
    await updateDoc(ref, {
      approved: selectedUser.approved,
      store: selectedUser.store,
      role: selectedUser.role,
    });

    await logUserAction(currentUser, "Updated User Role", {
      editedUser: selectedUser.email,
      newRole: selectedUser.role,
      newStore: selectedUser.store,
      approved: selectedUser.approved
    });

    await updateDoc(ref, {
      approved: selectedUser.approved,
      store: selectedUser.store,
      role: selectedUser.role,
    });
    setModalOpen(false);
    fetchUsers();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      await deleteDoc(doc(db, "users", id));
      fetchUsers();
    }
  };

  const sendReset = async (email) => {
    await sendPasswordResetEmail(auth, email);
    await sendPasswordResetEmail(auth, email);
    await logUserAction(currentUser, "Reset User Password", { targetUser: email });

    alert("Reset link sent to " + email);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setModalOpen(false);
      }
    };

    if (modalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen]);


  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">User Management</h1>
      {loading && <p className="mb-4 text-gray-500">Loading users...</p>}


      <div className="mb-4 flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
        {/* Search by email */}
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 border rounded w-full md:w-1/3"
        />

        {/* Filter by store */}
        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          className="px-3 py-2 border rounded w-full md:w-1/4"
        >
          <option value="">All Stores</option>
          {[...new Set(users.map(u => u.store).filter(Boolean))].sort().map(store => (
            <option key={store} value={store}>{store}</option>
          ))}
        </select>

        {/* Filter by role */}
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 border rounded w-full md:w-1/4"
        >
          <option value="">All Roles</option>
          {[...new Set(users.map(u => u.role).filter(Boolean))].sort().map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border">
          <thead>
            <tr className="bg-gray-100">
              {[
                ["Name", "name"],
                ["Email", "email"],
                ["Role", "role"],
                ["Store", "store"],
                ["Approved", "approved"],
                ["Last Login", "lastLogin"],
              ].map(([label, key]) => (
                <th
                  key={key}
                  onClick={() => sortBy(key)}
                  className="p-2 cursor-pointer text-left"
                >
                  {label} {sortKey === key ? (sortAsc ? "▲" : "▼") : ""}
                </th>
              ))}
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(user => (
              <tr key={user.id} className="border-t">
                <td className="p-2">{user.name || "-"}</td>
                {/* <td className="p-2">{user.email}</td> */}
                <td className="p-2">
                  <a
                    href={`/admin-logs?uid=${user.id}`}
                    className="text-indigo-600 hover:underline text-sm"
                  >
                    {user.email}
                  </a>
                </td>
                <td className="p-2">{user.role || "-"}</td>
                <td className="p-2">{user.store || "-"}</td>
                <td className="p-2">{user.approved ? "Yes" : "No"}</td>
                <td className="p-2 text-xs">
                  {user.lastLogin ? dayjs(user.lastLogin).format("MMM D, YYYY h:mm A") : "-"}
                </td>
                <td className="p-2 space-x-2">
                  <button
                    onClick={() => handleEdit(user)}
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => sendReset(user.email)}
                    className="text-indigo-600 hover:underline text-sm"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={(e) => {
            // Close modal if click happens on the backdrop
            if (e.target === e.currentTarget) {
              setModalOpen(false);
            }
          }}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()} // Prevent inside clicks from closing
          >
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-2 right-4 text-gray-500 hover:text-black text-2xl"
            >
              ×
            </button>

            <h2 className="text-lg font-bold mb-4">Edit User</h2>

            <label className="block mb-2">Role:</label>
            <select
              value={selectedUser.role || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
              className="border px-2 py-1 w-full mb-4"
            >
              <option disabled value="">-- Select Role --</option>
              <option value="Admin">Admin</option>
              <option value="Owner">Owner</option>
              <option value="Staff">Staff</option>
            </select>

            <label className="block mb-2">Store:</label>
            <select
              value={selectedUser.store || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, store: e.target.value })}
              className="border px-2 py-1 w-full mb-4"
            >
              <option disabled value="">-- Select Store --</option>
              <option value="Jean-Talon O">Jean-Talon O</option>
              <option value="Jean-Talon Market">Jean-Talon Market</option>
              <option value="Test">Test</option>
            </select>

            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={selectedUser.approved || false}
                onChange={(e) => setSelectedUser({ ...selectedUser, approved: e.target.checked })}
                className="mr-2"
              />
              Approved
            </label>

            <button
              onClick={handleSave}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminUsersPage;